import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Globe, Plus, RefreshCcw, CheckCircle2, XCircle, Pencil, Trash2,
  ImagePlus, ImageOff, ChevronDown, ChevronRight, Plug, RotateCcw,
} from "lucide-react";
import { api, getUser, isDemoWorkspace, type Site } from "../api";
import { SiteIntegrations } from "../components/SiteIntegrations";
import { ConfirmModal, PromptModal, useConfirm } from "../components/Modal";
import { Alert, PageHeader } from "../components/Page";

function isUrlShaped(v: string): boolean {
  try { const u = new URL(v.trim()); return u.protocol === "http:" || u.protocol === "https:"; }
  catch { return false; }
}

export function Sites() {
  const user = getUser();
  const isSuper = user?.role === "superadmin";
  const demo = isDemoWorkspace();

  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showDeleted, setShowDeleted] = useState(false);

  // Add-site form
  const [url, setUrl] = useState("");
  const [urlTouched, setUrlTouched] = useState(false);
  const [adding, setAdding] = useState(false);

  // Per-row busy states + modal state
  const [busyRow, setBusyRow] = useState<Record<string, string | null>>({});
  const [confirmDelete, setConfirmDelete] = useState<Site | null>(null);
  const [confirmRestore, setConfirmRestore] = useState<Site | null>(null);
  const [editUrlFor, setEditUrlFor] = useState<Site | null>(null);
  const [confirm, confirmNode] = useConfirm();

  const urlError = urlTouched && url.length > 0 && !isUrlShaped(url)
    ? "Enter a full URL starting with http:// or https://"
    : null;

  const refresh = useCallback(async () => {
    try {
      setSites(await api.listSites({ includeDeleted: showDeleted }));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally { setLoading(false); }
  }, [showDeleted]);
  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    if (!success) return;
    const t = window.setTimeout(() => setSuccess(null), 3500);
    return () => window.clearTimeout(t);
  }, [success]);

  function setRowBusy(id: string, label: string | null) {
    setBusyRow((s) => ({ ...s, [id]: label }));
  }

  async function addSite(e: React.FormEvent) {
    e.preventDefault();
    setUrlTouched(true);
    if (!isUrlShaped(url)) {
      setError("Enter a valid URL (including http:// or https://) before adding a site.");
      return;
    }
    setAdding(true); setError(null);
    try {
      await api.addSite(url.trim());
      setUrl(""); setUrlTouched(false);
      setSuccess("Site added.");
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally { setAdding(false); }
  }

  async function checkSnippet(s: Site) {
    setError(null); setRowBusy(s.id, "check");
    try { await api.checkSnippet(s.id); await refresh(); }
    catch (err) { setError(err instanceof Error ? err.message : String(err)); }
    finally { setRowBusy(s.id, null); }
  }
  async function crawl(s: Site) {
    setError(null); setRowBusy(s.id, "crawl");
    try { await api.crawlSite(s.id); setSuccess(`Crawl started for ${s.domain}.`); await refresh(); }
    catch (err) { setError(err instanceof Error ? err.message : String(err)); }
    finally { setRowBusy(s.id, null); }
  }
  async function submitEditUrl(values: Record<string, string>) {
    if (!editUrlFor) return;
    await api.updateSite(editUrlFor.id, values.url.trim());
    setSuccess(`URL updated for ${editUrlFor.domain}.`);
    setEditUrlFor(null);
    await refresh();
  }
  async function doDelete() {
    if (!confirmDelete) return;
    const s = confirmDelete;
    setConfirmDelete(null); setRowBusy(s.id, "delete");
    try {
      await api.deleteSite(s.id);
      setSuccess(`${s.domain} deleted. Its discovered PDFs are hidden until the site is restored.`);
      await refresh();
    } catch (err) { setError(err instanceof Error ? err.message : String(err)); }
    finally { setRowBusy(s.id, null); }
  }
  async function doRestore() {
    if (!confirmRestore) return;
    const s = confirmRestore;
    setConfirmRestore(null); setRowBusy(s.id, "restore");
    try {
      await api.restoreSite(s.id);
      setSuccess(`${s.domain} restored.`);
      await refresh();
    } catch (err) { setError(err instanceof Error ? err.message : String(err)); }
    finally { setRowBusy(s.id, null); }
  }

  const fileInputs = useRef<Record<string, HTMLInputElement | null>>({});
  const [expandedIntegrations, setExpandedIntegrations] = useState<Set<string>>(new Set());
  function toggleIntegrations(id: string) {
    setExpandedIntegrations((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  async function onLogoPicked(s: Site, ev: React.ChangeEvent<HTMLInputElement>) {
    const file = ev.target.files?.[0];
    ev.target.value = "";
    if (!file) return;
    setRowBusy(s.id, "logo");
    try { await api.uploadSiteLogo(s.id, file); setSuccess(`Logo uploaded for ${s.domain}.`); await refresh(); }
    catch (err) { setError(err instanceof Error ? err.message : String(err)); }
    finally { setRowBusy(s.id, null); }
  }
  async function clearLogo(s: Site) {
    const ok = await confirm({
      title: `Remove logo for ${s.domain}?`,
      message: "Reader transcripts for this site will fall back to the tenant logo (if any).",
      confirmLabel: "Remove logo",
      tone: "danger",
    });
    if (!ok) return;
    setRowBusy(s.id, "logo");
    try { await api.deleteSiteLogo(s.id); setSuccess(`Logo removed for ${s.domain}.`); await refresh(); }
    catch (err) { setError(err instanceof Error ? err.message : String(err)); }
    finally { setRowBusy(s.id, null); }
  }

  const liveCount = useMemo(() => sites.filter((s) => !s.deleted_at).length, [sites]);
  const deletedCount = useMemo(() => sites.filter((s) => !!s.deleted_at).length, [sites]);

  return (
    <div>
      <PageHeader
        title="Sites"
        description="Add your website, paste the helper-script snippet, then verify — SmartComply will crawl and convert every PDF."
        actions={<label className="inline-flex items-center gap-2 text-sm text-slate-600 select-none">
          <input
            type="checkbox"
            className="rounded border-slate-300 text-brand-500 focus:ring-brand-500"
            checked={showDeleted}
            onChange={(e) => setShowDeleted(e.target.checked)}
          />
          Show deleted sites {showDeleted && deletedCount > 0 && <span className="text-xs text-slate-400">({deletedCount})</span>}
        </label>}
      />

      {!isSuper && !demo && (
        <form onSubmit={addSite} className="card mb-6" noValidate>
          <div className="card-body flex items-end gap-3 flex-wrap">
            <div className="w-full sm:w-auto sm:flex-1 sm:min-w-[16rem]">
              <label htmlFor="new-site-url" className="block text-sm font-medium text-slate-700 mb-1">Add a site by homepage URL</label>
              <input
                id="new-site-url"
                className={`input ${urlError ? "!border-rose-400" : ""}`}
                placeholder="https://www.example.gov"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onBlur={() => setUrlTouched(true)}
                aria-invalid={!!urlError}
                aria-describedby={urlError ? "new-site-url-err" : undefined}
                required
              />
              {urlError && <p id="new-site-url-err" className="mt-1 text-xs text-rose-700">{urlError}</p>}
            </div>
            <button type="submit" className="btn-primary" disabled={adding}>
              {adding ? (<><span className="spinner" aria-hidden="true" /> Adding…</>) : (<><Plus className="h-4 w-4" /> Add site</>)}
            </button>
          </div>
        </form>
      )}

      {error && <Alert onDismiss={() => setError(null)}>{error}</Alert>}
      {success && <Alert tone="success" onDismiss={() => setSuccess(null)}>{success}</Alert>}

      <div className="card">
        {loading ? (
          <div className="card-body text-sm text-slate-500 flex items-center gap-2">
            <span className="spinner text-brand-500" aria-hidden="true" /> Loading sites…
          </div>
        ) : sites.length === 0 ? (
          <div className="card-body text-sm text-slate-500 flex items-center gap-2">
            <Globe className="h-4 w-4 text-slate-400" /> No sites yet.
          </div>
        ) : (
          <ul className="divide-y divide-slate-200">
            {sites.map((s) => {
              const isDeleted = !!s.deleted_at;
              const rowBusy = busyRow[s.id] || null;
              return (
                <li key={s.id} className={`px-4 sm:px-5 py-4 ${isDeleted ? "row-deleted" : ""}`}>
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="h-10 w-10 rounded border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden flex-shrink-0">
                        {s.logo_url
                          ? <img src={s.logo_url} alt={`${s.domain} logo`} className="max-h-10 max-w-10 object-contain" />
                          : <Globe className="h-4 w-4 text-slate-400" aria-hidden="true" />}
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium text-ink truncate flex items-center gap-2">
                          {s.domain}
                          {isDeleted && <span className="pill-deleted">deleted</span>}
                        </div>
                        <div className="text-xs text-slate-500 truncate">
                          <a href={s.homepage_url} target="_blank" rel="noopener noreferrer" className="hover:underline">{s.homepage_url}</a>
                          <span className="mx-2">·</span>
                          {s.last_scanned_at ? `${(s.pdf_count ?? s.last_crawl_pdf_count).toLocaleString()} PDFs · scanned ${new Date(s.last_scanned_at).toLocaleString()}` : "not scanned yet"}
                        </div>
                      </div>
                    </div>

                    {isDeleted ? (
                      // Deleted rows: only the Restore action stays live.
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => setConfirmRestore(s)}
                          className="btn-secondary"
                          disabled={rowBusy === "restore"}
                          style={{ pointerEvents: "auto" }}
                        >
                          {rowBusy === "restore"
                            ? <><span className="spinner" aria-hidden="true" /> Restoring…</>
                            : <><RotateCcw className="h-4 w-4" /> Restore</>}
                        </button>
                      </div>
                    ) : demo ? (
                      <span className={`pill-${s.status}`}>{s.status}</span>
                    ) : (
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className={`pill-${s.status}`}>{s.status}</span>
                        <span
                          className="inline-flex items-center gap-1 text-xs text-slate-500"
                          title={s.helper_script_installed ? "Helper script detected on homepage" : "Helper script not detected — visitors won't see the Accessible badge yet"}
                        >
                          {s.helper_script_installed
                            ? <><CheckCircle2 className="h-4 w-4 text-emerald-600" /> snippet active</>
                            : <><XCircle className="h-4 w-4 text-slate-400" /> snippet not detected</>}
                        </span>
                        <button onClick={() => checkSnippet(s)} className="btn-ghost" disabled={rowBusy === "check"} title="Re-check whether the helper script is loaded on the homepage">
                          {rowBusy === "check" ? <><span className="spinner" aria-hidden="true" /> Checking…</> : "Check snippet"}
                        </button>
                        <button onClick={() => crawl(s)} className="btn-secondary" disabled={s.status === "crawling" || rowBusy === "crawl"}>
                          {(s.status === "crawling" || rowBusy === "crawl")
                            ? <><span className="spinner" aria-hidden="true" /> Crawling…</>
                            : <><RefreshCcw className="h-4 w-4" /> Crawl</>}
                        </button>
                        <input
                          ref={(el) => { fileInputs.current[s.id] = el; }}
                          type="file"
                          accept="image/png,image/jpeg,image/svg+xml,image/webp,image/gif"
                          className="hidden"
                          onChange={(ev) => onLogoPicked(s, ev)}
                        />
                        <button
                          onClick={() => fileInputs.current[s.id]?.click()}
                          className="btn-ghost"
                          disabled={rowBusy === "logo"}
                          aria-label={`${s.logo_url ? "Replace" : "Upload"} logo for ${s.domain}`}
                          title={s.logo_url ? "Replace site logo" : "Upload site logo"}
                        >
                          {rowBusy === "logo" ? <span className="spinner" aria-hidden="true" /> : <ImagePlus className="h-4 w-4" />}
                        </button>
                        {s.logo_url && (
                          <button
                            onClick={() => clearLogo(s)}
                            className="btn-ghost"
                            aria-label={`Remove logo for ${s.domain}`}
                            title="Remove site logo"
                          >
                            <ImageOff className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => toggleIntegrations(s.id)}
                          className="btn-ghost"
                          aria-expanded={expandedIntegrations.has(s.id)}
                          title="WordPress connector & visitor tracker"
                        >
                          {expandedIntegrations.has(s.id) ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          <Plug className="h-4 w-4" /> Connectors
                        </button>
                        <button onClick={() => setEditUrlFor(s)} className="btn-ghost" aria-label={`Edit URL for ${s.domain}`}>
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button onClick={() => setConfirmDelete(s)} className="btn-ghost text-rose-600" disabled={rowBusy === "delete"} aria-label={`Delete ${s.domain}`}>
                          {rowBusy === "delete" ? <span className="spinner" aria-hidden="true" /> : <Trash2 className="h-4 w-4" />}
                        </button>
                      </div>
                    )}
                  </div>
                  {expandedIntegrations.has(s.id) && !isDeleted && <SiteIntegrations site={s} />}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {showDeleted && liveCount === 0 && deletedCount > 0 && (
        <p className="mt-3 text-xs text-slate-500">
          All sites here are soft-deleted. Uncheck "Show deleted sites" to hide them, or click Restore to bring one back.
        </p>
      )}

      {/* ─── Modals ─── */}

      <ConfirmModal
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={doDelete}
        title={`Delete ${confirmDelete?.domain}?`}
        message={<>Removes <strong>{confirmDelete?.domain}</strong> and hides all of its discovered PDFs. You can bring it back later using the "Show deleted sites" toggle.</>}
        confirmLabel="Delete site"
        tone="danger"
      />
      <ConfirmModal
        open={!!confirmRestore}
        onClose={() => setConfirmRestore(null)}
        onConfirm={doRestore}
        title={`Restore ${confirmRestore?.domain}?`}
        message={<>Brings <strong>{confirmRestore?.domain}</strong> back and un-hides the discovered PDFs that were removed when it was deleted.</>}
        confirmLabel="Restore site"
      />
      <PromptModal
        open={!!editUrlFor}
        onClose={() => setEditUrlFor(null)}
        onSubmit={submitEditUrl}
        title={`Edit URL — ${editUrlFor?.domain ?? ""}`}
        description="Changing the homepage URL updates the domain we look up and the page we probe for the helper script."
        fields={useMemo(() => [{
          name: "url",
          label: "Homepage URL",
          type: "url",
          initialValue: editUrlFor?.homepage_url || "",
          required: true,
          validate: (v) => (isUrlShaped(v) ? null : "Enter a full URL starting with http:// or https://"),
        }], [editUrlFor])}
        submitLabel="Save URL"
      />

      {confirmNode}
    </div>
  );
}
